import { type HTMLAttributes } from 'react';

import { cn } from '../../lib/cn';

/**
 * docs/02-DESIGN-SYSTEM.md §4.1 — "Skeletons required for every async list —
 * no layout shift", and docs/06-FRONTEND-SPEC.md §7: skeletons matching the
 * final layout for anything slower than 400 ms.
 *
 * Hidden from assistive technology. A screen reader announcing a dozen grey
 * rectangles is worse than silence — the surrounding region carries the
 * loading status (see `Spinner`).
 *
 * The pulse is disabled under `prefers-reduced-motion` by base.css.
 */
export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** `text` rounds to the line height; `rect` and `circle` are for media. */
  shape?: 'text' | 'rect' | 'circle';
}

const SHAPES = {
  text: 'rounded-sm h-4',
  rect: 'rounded-lg',
  circle: 'rounded-full',
} as const;

export function Skeleton({ shape = 'text', className, ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse bg-surface-alt', SHAPES[shape], className)}
      {...props}
    />
  );
}
